/**
 * Walkthrough visibility policy — reuses nav RBAC semantics.
 * @module features/walkthrough/walkthroughPolicy
 */

import { buildNavContext, NAV_SECTIONS } from '../../navigation/navConfig';
import { ALL_WALKTHROUGH_TOURS } from './tours/tourRegistry';
import { isTourCompleted } from './walkthroughStorage';

/** @type {Map<string, (user: object, ctx: import('../../navigation/navConfig').NavContext) => boolean>} */
const navItemVisibilityById = new Map();

for (const section of NAV_SECTIONS) {
  for (const item of section.items) {
    navItemVisibilityById.set(item.id, item.visible);
  }
}

/**
 * @param {import('./walkthroughTypes').WalkthroughStepDef} step
 * @param {object|null|undefined} user
 * @param {import('../../navigation/navConfig').NavContext} ctx
 * @returns {boolean}
 */
export function isStepVisibleForUser(step, user, ctx) {
  if (step.visible) {
    return step.visible(user, ctx);
  }

  if (step.navItemId) {
    const navVisible = navItemVisibilityById.get(step.navItemId);
    if (navVisible) {
      return navVisible(user, ctx);
    }
  }

  return true;
}

/**
 * @param {import('./walkthroughTypes').WalkthroughStepDef[]} steps
 * @param {object|null|undefined} user
 * @param {import('../../navigation/navConfig').NavContext} [navContext]
 * @returns {import('./walkthroughTypes').ResolvedWalkthroughStep[]}
 */
export function filterStepsForUser(steps, user, navContext) {
  if (!user || !Array.isArray(steps)) return [];

  const ctx = navContext ?? buildNavContext(user);

  return steps
    .filter((step) => isStepVisibleForUser(step, user, ctx))
    .map((step, index) => ({ ...step, index }));
}

/**
 * @param {object|null|undefined} user
 * @param {import('../../navigation/navConfig').NavContext} [navContext]
 * @param {import('./walkthroughTypes').WalkthroughTourDef[]} [tours]
 * @returns {import('./walkthroughTypes').ResolvedWalkthroughTour[]}
 */
export function resolveVisibleTours(user, navContext, tours = ALL_WALKTHROUGH_TOURS) {
  if (!user || !Array.isArray(tours)) return [];

  const ctx = navContext ?? buildNavContext(user);

  return tours
    .filter((tour) => {
      if (tour.visible && !tour.visible(user, ctx)) {
        return false;
      }
      return filterStepsForUser(tour.steps, user, ctx).length > 0;
    })
    .map((tour) => ({
      ...tour,
      steps: filterStepsForUser(tour.steps, user, ctx),
    }));
}

/**
 * Tours the user has not completed (or whose version was bumped).
 * @param {object|null|undefined} user
 * @param {import('./walkthroughTypes').WalkthroughPersistedState} persistedState
 * @param {import('../../navigation/navConfig').NavContext} [navContext]
 * @param {import('./walkthroughTypes').WalkthroughTourDef[]} [tours]
 * @returns {import('./walkthroughTypes').ResolvedWalkthroughTour[]}
 */
export function resolvePendingTours(
  user,
  persistedState,
  navContext,
  tours = ALL_WALKTHROUGH_TOURS
) {
  return resolveVisibleTours(user, navContext, tours).filter(
    (tour) => !isTourCompleted(persistedState, tour)
  );
}
